import { Lightning } from '@lightningjs/sdk';
import { Helper } from '../lib/helper';

export default class VideoTimer extends Lightning.Component {
  static override _template() {
    const digitStyle = {
      w: 12, // Strict dimensional grid bounds
      text: { text: '0', fontSize: 20, textAlign: 'center', fontFace: Helper.getFontFamily('Black') }
    };

    return {
      flex: { direction: 'row', alignItems: 'center' },

      // --- Current Time Matrix ---
      CurrentHours: {
        flex: { direction: 'row' },
        H1: { ...digitStyle },
        H2: { ...digitStyle },
        HSep: {
          w: 8,
          text: { text: ':', fontSize: 20, textAlign: 'center', fontFace: Helper.getFontFamily('Black') }
        }
      },
      M1: { ...digitStyle },
      M2: { ...digitStyle },
      Separator: {
        w: 8,
        text: { text: ':', fontSize: 20, textAlign: 'center', fontFace: Helper.getFontFamily('Black') }
      },
      S1: { ...digitStyle },
      S2: { ...digitStyle },

      // --- Static Divider ---
      Divider: {
        w: 16,
        text: { text: '|', fontSize: 20, textAlign: 'center', fontFace: Helper.getFontFamily('Black') }
      },

      // --- Total Duration Matrix ---
      DurationHours: {
        flex: { direction: 'row' },
        DH1: { ...digitStyle },
        DH2: { ...digitStyle },
        DHSep: {
          w: 8,
          text: { text: ':', fontSize: 20, textAlign: 'center', fontFace: Helper.getFontFamily('Black') }
        }
      },
      DM1: { ...digitStyle },
      DM2: { ...digitStyle },
      DSeparator: {
        w: 8,
        text: { text: ':', fontSize: 20, textAlign: 'center', fontFace: Helper.getFontFamily('Black') }
      },
      DS1: { ...digitStyle, text: { ...digitStyle.text} },
      DS2: { ...digitStyle, text: { ...digitStyle.text} }
    };
  }

  override _init() {
    // 1. Force the TextureManager cache to build permanent lookups for digits 0-9
    for (let i = 0; i <= 9; i++) {
      this.stage.textureManager.getTextureSource(
        new Lightning.textures.TextTexture(this.stage, { text: i.toString(), fontSize: 20, textAlign: 'center', fontFace: Helper.getFontFamily('Black')})
      );
    }
    
    this.reset();
  }

  /**
   * Updates playback progress without generating textures
   * @param {number} seconds 
   */
  updateProgress(seconds) {
    const [h1, h2, m1, m2, s1, s2] = this._sliceTime(seconds);
    
    this.tag('H1').text = h1;
    this.tag('H2').text = h2;
    this.tag('M1').text = m1;
    this.tag('M2').text = m2;
    this.tag('S1').text = s1;
    this.tag('S2').text = s2;
  }

  /**
   * Sets total duration and handles conditional layouts
   * @param {number} seconds 
   */
  setDuration(seconds) {
    const [dh1, dh2, dm1, dm2, ds1, ds2] = this._sliceTime(seconds);
    
    this.tag('DH1').text = dh1;
    this.tag('DH2').text = dh2;
    this.tag('DM1').text = dm1;
    this.tag('DM2').text = dm2;
    this.tag('DS1').text = ds1;
    this.tag('DS2').text = ds2;

    // 2. Structural visibility switch: Hide hours layout block if video is under 1 hour (3600 seconds)
    const showHours = seconds >= 3600;
    this.tag('CurrentHours').patch({ visible: showHours });
    this.tag('DurationHours').patch({ visible: showHours });
  }

  reset() {
    this.updateProgress(0);
    this.setDuration(0);
  }

  /**
   * High performance atomic calculation for matrix separation
   * @returns {string[]} ex: ["0", "2", "1", "4", "0", "5"] for 02:14:05
   */
  _sliceTime(totalSeconds) {
    if (isNaN(totalSeconds) || totalSeconds < 0) return ['0', '0', '0', '0', '0', '0'];
    
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);

    const padHrs = hrs.toString().padStart(2, '0');
    const padMins = mins.toString().padStart(2, '0');
    const padSecs = secs.toString().padStart(2, '0');

    return [
      padHrs[0],  // H1
      padHrs[1],  // H2
      padMins[0], // M1
      padMins[1], // M2
      padSecs[0], // S1
      padSecs[1]  // S2
    ];
  }
}
